import { useState } from 'react';
import { Copy, Check, Info, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ConnectionStringModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionString: string;
}

type ConnectionType = 'URI' | 'PSQL' | 'Golang' | 'JDBC' | '.NET' | 'Node.js' | 'PHP' | 'Python' | 'SQLAlchemy';

interface ParsedConnection {
  user: string;
  password: string;
  host: string;
  port: string;
  database: string;
}

export function ConnectionStringModal({ isOpen, onClose, connectionString }: ConnectionStringModalProps) {
  const [selectedType, setSelectedType] = useState<ConnectionType>('URI');
  const [copied, setCopied] = useState(false);

  const parseConnectionString = (uri: string): ParsedConnection | null => {
    try {
      const match = uri.match(/postgresql:\/\/([^:]+):([^@]+)@([^:]+):(\d+)\/(.+)/);
      if (!match) return null;

      return {
        user: match[1],
        password: match[2],
        host: match[3],
        port: match[4],
        database: match[5],
      };
    } catch {
      return null;
    }
  };

  const parsed = parseConnectionString(connectionString);

  const maskPassword = (str: string): string => {
    if (!parsed) return str;
    return str.replace(parsed.password, '[your-password]');
  };

  const getConnectionString = (): string => {
    if (!parsed) return connectionString;

    const { user, password, host, port, database } = parsed;

    switch (selectedType) {
      case 'URI':
        return connectionString;

      case 'PSQL':
        return `psql "postgresql://${user}:${password}@${host}:${port}/${database}"`;

      case 'Golang':
        return `host=${host} port=${port} user=${user} password=${password} dbname=${database} sslmode=require`;

      case 'JDBC':
        return `jdbc:postgresql://${host}:${port}/${database}?user=${user}&password=${password}`;

      case '.NET':
        return `Host=${host};Port=${port};Username=${user};Password=${password};Database=${database};SslMode=Require;`;

      case 'Node.js':
        return `const { Pool } = require('pg');

const pool = new Pool({
  user: '${user}',
  password: '${password}',
  host: '${host}',
  port: ${port},
  database: '${database}',
  ssl: { rejectUnauthorized: false }
});`;

      case 'PHP':
        return `$conn = pg_connect("
  host=${host}
  port=${port}
  dbname=${database}
  user=${user}
  password=${password}
  sslmode=require
");`;

      case 'Python':
        return `import psycopg2

conn = psycopg2.connect(
    host="${host}",
    port=${port},
    database="${database}",
    user="${user}",
    password="${password}",
    sslmode="require"
)`;

      case 'SQLAlchemy':
        return `from sqlalchemy import create_engine

engine = create_engine(
    "postgresql://${user}:${password}@${host}:${port}/${database}?sslmode=require"
)`;

      default:
        return connectionString;
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getConnectionString());
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const types: ConnectionType[] = ['URI', 'PSQL', 'Golang', 'JDBC', '.NET', 'Node.js', 'PHP', 'Python', 'SQLAlchemy'];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Connection String</DialogTitle>
          <DialogDescription>
            Connect to your database using the connection string below
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Config row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</label>
              <Select value={selectedType} onValueChange={(v) => setSelectedType(v as ConnectionType)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Method</label>
              <div className="h-9 flex items-center px-3 rounded-md border border-input bg-muted/30 text-sm">
                Session Pooler
              </div>
            </div>
          </div>

          {/* Connection string code block */}
          <div className="relative rounded-lg border border-border bg-muted/30 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border/60 bg-muted/40">
              <span className="text-xs text-muted-foreground font-medium">Connection String</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs px-2.5 gap-1.5"
                onClick={copyToClipboard}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <pre className="p-4 text-[13px] font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
              {maskPassword(getConnectionString())}
            </pre>
          </div>

          {/* Notes */}
          <div className="space-y-2.5">
            <div className="flex gap-2.5 rounded-lg border border-border/60 bg-muted/20 p-3">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Session Pooler</span> -- ideal for serverless functions where the execution context is brief and local state is lost on completion.
              </p>
            </div>

            {parsed && (
              <div className="flex gap-2.5 rounded-lg border border-border/60 bg-muted/20 p-3">
                <ShieldAlert className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <span className="font-medium text-foreground">Security</span> -- this connection string contains your database password. Never commit credentials to version control. Use environment variables in production.
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
